import styles from "./manage.page.module.css";
import { Shelf } from "../components/manage/Shelf";
import { useManagePageLogic } from "../features/manage/hooks/useManagePageLogic";
import { Button } from "../components/Button";
import { ErrorMessage } from "../components/ErrorMessage";
import { HelpText } from "../components/HelpText";
import { InventoryColumn } from "../features/manage/components/InventoryColumn";
import { QuickAddForm } from "../features/manage/components/QuickAddForm";
import { ShelfNavigator } from "../features/manage/components/ShelfNavigator";
import { useMode } from "../context/ModeContext";
import { useEffect, useRef } from "react";
import { allWarehouseHeights } from "../constants";
import { InventoryColumn2 } from "../features/manage/components/InventoryColumn2";

export const ManagePage = () => {
  const { data, ui, actions } = useManagePageLogic();
  const { navigationPayload } = useMode();

  const quantityInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (navigationPayload) {
      const matchedShelf = allWarehouseHeights.find(
        (s) => s.shelf === navigationPayload || s.shelf.startsWith(navigationPayload)
      );
      if (matchedShelf) {
        actions.selectShelf(matchedShelf.shelf);
      }
    }
  }, [navigationPayload]);

  const handleItemClick = (ean: string) => {
    const dbItem = data.databaseData?.results.find((o) => o.ean === ean);
    const newItem = data.newData.find((o) => o.ean === ean);
    if (!dbItem) {
      actions.removeEan(ean);
      return;
    }
    const hasNewEntry = newItem !== undefined;
    if (hasNewEntry) {
      actions.removeEan(ean);
    } else {
      actions.addEan(ean, dbItem.quantity.toString());
    }
  };

  const handleEditItem = (ean: string, currentQty: number) => {
    actions.setEanField(ean);
    actions.setQuantityField(currentQty.toString());
    setTimeout(() => {
      quantityInputRef.current?.focus();
    }, 0);
  };

  const isSingleManageMode = localStorage.getItem("single_manage_mode") === "true";

  return (
    <>
      <ShelfNavigator
        selectedShelf={data.selectedShelf}
        groups={data.groups}
        onSelect={actions.selectShelf}
        onNext={actions.selectNextShelf}
      />
      {data.selectedShelf && (
        <>
          <div className={styles.shelfSection}>
            <Shelf
              shelf={data.currentShelfData}
              selectedLocation={data.selectedLocation}
              onSelect={actions.selectLocation}
            />
          </div>
          {!data.selectedLocation && (
            <HelpText alwaysVisible>Wybierz miejsce, żeby wyświetlić zawartość palety.</HelpText>
          )}
        </>
      )}
      <div className={styles.mainContentArea}>
        {data.selectedLocation && (
          <div className={`${styles.dataContainer} ${data.isSyncing ? styles.loadingOverlay : ""}`}>
            <div className={styles.compareContainer}>
              {!isSingleManageMode && (
                <>
                  <InventoryColumn2
                    title="Aktualny stan"
                    isLoading={data.showSkeleton}
                    items={data.databaseData?.results || []}
                    databaseItems={data.databaseData?.results || []}
                    newItems={data.newData}
                    onItemClick={(ean, qty) => actions.addEan(ean, qty.toString())}
                  />
                  <InventoryColumn2
                    title="Nowy stan"
                    isLoading={data.showSkeleton}
                    items={[...data.newData].sort((a, b) => a.ean.localeCompare(b.ean))}
                    databaseItems={data.databaseData?.results || []}
                    newItems={data.newData}
                    onItemClick={(ean) => actions.removeEan(ean)}
                    badgeStatus={(item) => {
                      const old = data.databaseData?.results.find((o) => o.ean === item.ean);
                      if (!old || item.quantity > old.quantity) return "plus";
                      if (item.quantity < old.quantity) return "minus";
                      return "neutral";
                    }}
                  >
                    {!data.showSkeleton && (
                      <QuickAddForm
                        ean={ui.eanField}
                        quantity={ui.quantityField}
                        onEanChange={actions.setEanField}
                        onQuantityChange={actions.setQuantityField}
                        onAdd={() => actions.addEan(ui.eanField, ui.quantityField)}
                        isOverwriteQuantities={data.overwriteEANquantity}
                      />
                    )}
                  </InventoryColumn2>
                </>
              )}
              {isSingleManageMode && (
                <InventoryColumn
                  title="ZMIANY"
                  isLoading={data.showSkeleton}
                  databaseItems={data.databaseData?.results || []}
                  newItems={data.newData}
                  onItemClick={handleItemClick}
                  onEditItem={handleEditItem}
                >
                  {!data.showSkeleton && (
                    <QuickAddForm
                      ean={ui.eanField}
                      quantity={ui.quantityField}
                      onEanChange={actions.setEanField}
                      onQuantityChange={actions.setQuantityField}
                      onAdd={() => actions.addEan(ui.eanField, ui.quantityField)}
                      isOverwriteQuantities={data.overwriteEANquantity}
                      quantityInputRef={quantityInputRef}
                    />
                  )}
                </InventoryColumn>
              )}
            </div>
            <Button
              disabled={data.isSame || data.isSyncing || data.showSkeleton}
              onClick={actions.saveChanges}
              text={ui.enabledConfirmButton ? "POTWIERDŹ ZAPIS" : "ZAPISZ ZMIANY"}
            />
            <div className={styles.checkboxContainer}>
              <label className={styles.copyLabel}>
                <input
                  className={styles.copyInput}
                  type="checkbox"
                  checked={data.autoFill}
                  onChange={actions.toggleAutoFill}
                />
                Zaczynaj z aktualnym stanem
              </label>
              <label className={styles.copyLabel}>
                <input
                  className={styles.copyInput}
                  type="checkbox"
                  checked={data.overwriteEANquantity}
                  onChange={actions.toggleOverwriteQuantities}
                  // onChange={() => actions.setOverwriteEANquantity(!data.overwriteEANquantity)}
                />
                Nadpisuj ilości
              </label>
            </div>
          </div>
        )}
      </div>
      {ui.errorMessage && <ErrorMessage>{ui.errorMessage}</ErrorMessage>}
      {data.isSlowConnection && <ErrorMessage>Słabe połączenie internetowe.</ErrorMessage>}
    </>
  );
};
